import { defaults, mapObject, constant, compact, isFunction, findIndex, find, extend, isUndefined } from 'underscore';
import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';
import $ from 'jquery';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { Dropdown } from '@bingads-webui-react/primitive';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  withFlatToolsSiteMap, withImportSiteMap, withFlatSmartImportSiteMap,
  withReportsSiteMap, processor,
} from '@bingads-webui-react/with-site-map';
import { Callout } from '@bingads-webui-react/callout';
import { useId } from '@fluentui/react-hooks';
import { StyledGlobalSearchWithoutHotKeyOrClickOutside as StyledGlobalSearch } from '@bingads-webui-react/global-search';
import { withActivityContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withHotKeys, ShortcutType } from '@bingads-webui-react/hot-keys';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { getToolbarElementsStyles } from '../../styles';
import { StyledHeader } from './header';
import { renderMenu } from './render-menu';
import { ReturnMenuItem } from './return-menu-item';
import { CustomizedDropdown, CustomizedDropdownWithRef } from './customized-dropdown';
import { MultiLevelDropDown } from './multilevel-dropdown';
import { getButtonClassNames, createToolbarButton } from './utilities';

const structuredSiteMap = siteMap => ({ children: [...siteMap] });
const getRegion = i18n => i18n.getCldr().attributes.region;

const UnthemedImportBar = withI18nConsumer(({
  classes, i18n, siteMap, responsiveSize,
}) => {
  const renderDropdownToggle = () => (
    <Dropdown.Toggle
      className={getButtonClassNames(
        classes,
        ['global-menu-import-dropdown-toggle'],
        responsiveSize
      )}
      noCaret
      bsStyle="primary"
      aria-label={i18n.getString(_TL_('Import campaigns'))}
    >
      <StyledHeader
        glyph="ba-Import"
        text={i18n.getString(_TL_('Import'))}
        responsiveSize={responsiveSize}
      />
    </Dropdown.Toggle>
  );

  const formattedSiteMap = structuredSiteMap(siteMap);
  // add divider before history item.
  formattedSiteMap.children.splice(
    findIndex(formattedSiteMap.children, item => item.id === 'import-schedule'),
    0,
    {
      id: 'divider',
      name: 'Divider',
      children: [],
    }
  );

  // deal with import center item
  const importCenterSiteMap = find(formattedSiteMap.children, item => item.id === 'import-center');
  if (!isUndefined(importCenterSiteMap)) {
    extend(importCenterSiteMap, { children: [] });
    formattedSiteMap.children.splice(
      findIndex(formattedSiteMap.children, item => item.id === 'import-center'),
      0,
      {
        id: 'import-center-divider',
        name: 'Divider',
        children: [],
      }
    );
  }

  // Empty div is needed here when wrapping menu items to achieve readability of Dropdown control.
  const renderDropdownMenu = toggleDropdown => (
    <Dropdown.Menu
      className={classNames([classes.menuContainer, 'global-menu-import-dropdown-menu'])}
    >
      <div>
        {renderMenu({
          classes,
          menuData: formattedSiteMap,
          responsiveSize,
          i18n,
          closeDropdown: toggleDropdown,
          siteMap,
          topLevel: 0,
        })}
      </div>
    </Dropdown.Menu>
  );

  return (
    <CustomizedDropdown
      id="import"
      renderDropdownToggle={renderDropdownToggle}
      renderDropdownMenu={renderDropdownMenu}
      shouldFocusOnContainer="true"
      doNotLayer="true"
    />
  );
});

const UnthemedReportsBar = withI18nConsumer(({
  classes, i18n, siteMap, responsiveSize,
}) => (
  <MultiLevelDropDown
    classes={classes}
    i18n={i18n}
    siteMap={siteMap}
    responsiveSize={responsiveSize}
  />
));


const getUnthemedToolsBar = (TopMenuComponent = ReturnMenuItem) => withI18nConsumer(({
  classes, i18n, siteMap, responsiveSize,
}) => {
  const verticalMenu = responsiveSize !== 'xxl';

  const renderDropdownToggle = () => (
    <Dropdown.Toggle
      className={getButtonClassNames(
        classes,
        ['global-menu-tools-dropdown-toggle'],
        responsiveSize
      )}
      noCaret
      bsStyle="primary"
    >
      <StyledHeader
        glyph="ba-Repair"
        text={i18n.getString(_TL_('Tools'))}
        responsiveSize={responsiveSize}
      />
    </Dropdown.Toggle>
  );
  const formattedSiteMap = structuredSiteMap(siteMap);

  const renderDropdownMenu = toggleDropdown => (
    <Dropdown.Menu
      className={classNames([
        classes.menuContainer,
        classes.menuContainerOffset,
        'global-menu-tools-dropdown-menu'])}
      style={(verticalMenu ? {} : { right: '-106px' })}
    >
      <div className={classes.toolsMenuContainer}>
        <TopMenuComponent closeDropdown={toggleDropdown} />
        <div
          className={
            verticalMenu ? classes.twoLevelMenuContainerVertical :
              classes.twoLevelMenuContainerHorizontal
          }
        >
          {renderMenu({
            classes,
            menuData: formattedSiteMap,
            responsiveSize,
            i18n,
            closeDropdown: toggleDropdown,
            siteMap,
            topLevel: 0,
          })}
        </div>
      </div>
    </Dropdown.Menu>
  );

  return (
    <CustomizedDropdown
      id="tools"
      renderDropdownToggle={renderDropdownToggle}
      renderDropdownMenu={renderDropdownMenu}
    />
  );
});

const UnthemedHelpBar = withPermissions(withI18nConsumer(({
  classes,
  i18n,
  siteMap,
  responsiveSize,
  feedbackProps,
  helpProps,
}) => {
  const activity = useContext(ActivityContext);
  const [isOpen, setOpen] = useState(false);
  const globalHelpRef = useRef(null);
  const getStartedHelpRef = useRef(null);
  const dropdownRef = useRef(null);

  const openFeedbackPanel = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} open feedback panel triggered`);
    }
    setOpen(true);
  }, [activity, i18n]);

  const closeFeedbackPanel = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} close feedback panel triggered`);
    }
    setOpen(false);
  }, [activity, i18n]);

  const renderDropdownToggle = () => (
    <Dropdown.Toggle
      className={getButtonClassNames(
        classes,
        ['global-menu-help-dropdown-toggle'],
        responsiveSize
      )}
      noCaret
      bsStyle="primary"
      aria-label={i18n.getString(_TL_('Help'))}
    >
      <StyledHeader
        glyph="ba-Help"
        text={i18n.getString(_TL_('Help'))}
        responsiveSize={responsiveSize}
      />
    </Dropdown.Toggle>
  );

  const {
    showGetHelp,
    showGetStarted,
    showCommunity,
    showLegacyChat,
    customizedMenuItems,
  } = helpProps;

  const {
    isTestEnv,
    email,
    pageId,
    sessionId,
    feedbackLogger,
    appId,
    hideFeedback,
    featureArea,
  } = feedbackProps;

  const returnFocus = useCallback(() => () => {
    if (dropdownRef.current && isFunction(dropdownRef.current.focus)) {
      dropdownRef.current.focus();
    }
  }, []);

  const browserHelpTrigger = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} global help articles triggered`);
    }
    globalHelpRef.current.click();
  }, [activity, i18n]);

  const getStartedTrigger = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} get started triggered`);
    }
    getStartedHelpRef.current.click();
  }, [activity, i18n]);

  const showCommunityTrigger = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} show community triggered`);
    }
    window.open('https://answers.microsoft.com/lang/msadvs/forum/', '_blank');
  }, [activity, i18n]);

  const chatTrigger = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} live chat triggered`);
    }
    $('#msSupportChatTrigger').trigger('click', { onCancel: returnFocus });
  }, [activity, i18n, returnFocus]);

  const chatCopilotTrigger = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} copilot live agent support triggered`);
    }

    const detail = {
      eventType: 'action',
      eventData: {
        ActionId: 'liveAgentSupport',
      },
    };

    document.dispatchEvent(new CustomEvent('copilotToggleEvent', { detail }));
  }, [activity, i18n]);

  const callTrigger = useCallback(() => {
    if (activity) {
      activity.signal(`${getRegion(i18n)} request a call triggered`);
    }
    $('#requestCallTrigger').trigger('click', { onCancel: returnFocus });
  }, [activity, i18n, returnFocus]);

  const menuData = () => ({
    children: compact([
      showGetHelp ? {
        id: 'get-help',
        name: _TL_('Browse help articles'),
        children: [],
        onClick: browserHelpTrigger,
      } : null,
      showGetStarted ? {
        id: 'getting-started',
        name: _TL_('Get started'),
        onClick: getStartedTrigger,
        children: [],
      } : null,
      showCommunity ? {
        id: 'community',
        name: _TL_('Ask the community'),
        onClick: showCommunityTrigger,
        children: [],
      } : null,
      ...customizedMenuItems,
      !showLegacyChat ? {
        id: 'live-chat',
        name: _TL_('Start a live chat'),
        onClick: chatCopilotTrigger,
        children: [],
      } : null,
      (showLegacyChat && $('#msSupportChatTrigger').length > 0) ? {
        id: 'live-chat',
        name: _TL_('Start a live chat'),
        onClick: chatTrigger,
        children: [],
      } : null,
      $('#requestCallTrigger').length > 0 ? {
        id: 'request-call',
        name: _TL_('Request a call from support'),
        onClick: callTrigger,
        children: [],
      } : null,
      hideFeedback ? null : {
        id: 'send-feedback',
        name: _TL_('Send us feedback'),
        onClick: openFeedbackPanel,
        children: [],
      },
    ]),
  });

  const defaultTestAppId = 50025;
  const defaultAppId = 2482;

  const FeedbackPanelComponent = React.lazy(() => import(/* webpackMode: 'eager' */ '@bingads-webui-react/feedback-panel').then(({ FeedbackPanel }) => ({
    default: () => (
      <FeedbackPanel
        appId={appId || (isTestEnv ? defaultTestAppId : defaultAppId)}
        isTestEnv={isTestEnv}
        isOpen={isOpen}
        dismissPanel={closeFeedbackPanel}
        i18n={i18n}
        instrumentationService={feedbackLogger || { log: () => { } }}
        email={email}
        pageId={pageId}
        sessionId={sessionId}
        featureArea={featureArea}
      />),
  })));

  const renderDropdownMenu = toggleDropdown => (
    <Dropdown.Menu
      className={classNames([
        classes.menuContainer,
        classes.menuContainerOffset,
        'global-menu-help-dropdown-menu',
      ])}
    >
      <div>
        {renderMenu({
          classes,
          menuData,
          responsiveSize,
          i18n,
          closeDropdown: toggleDropdown,
          siteMap,
          topLevel: 0,
        })}
        <div style={{ display: 'none' }}>
          <a ref={globalHelpRef} className="HelpTopic" href="/#" > </a>
          <a ref={getStartedHelpRef} className="HelpTopic" href="/#" query="ext56873"> </a>
        </div>
        <React.Suspense fallback={null}>
          {isOpen && <FeedbackPanelComponent />}
        </React.Suspense>
      </div>
    </Dropdown.Menu>
  );

  return (
    <div>
      <CustomizedDropdownWithRef
        id="help"
        renderDropdownToggle={renderDropdownToggle}
        renderDropdownMenu={renderDropdownMenu}
        shouldFocusOnContainer="true"
        doNotLayer="true"
        ref={dropdownRef}
      />
    </div>
  );
}));

export const ExpandBar = createToolbarButton(['global-menu-expand-dropdown-toggle'], 'ba-More');

export const SearchBar = createToolbarButton(['global-menu-search-dropdown-toggle'], 'ba-Search');

export const HelpBarWithoutFeedback = createToolbarButton(['global-menu-help-dropdown-toggle', 'HelpTopic'], 'ba-Help');

const UnthemedSearchDropdownButton = withActivityContext(withI18nConsumer(withHotKeys(({
  classes, i18n, siteMap, responsiveSize, renderSearchResults, hotKey, count, onCopilotClick, serverContext,
}) => {
  const activity = useContext(ActivityContext);
  const [isCalloutVisible, setCalloutVisible] = React.useState(false);
  const buttonId = useId('global-search-dropdown-button');
  const toggleIsCalloutVisible = useCallback(() => {
    if (activity) {
      activity.signal('top nav -  search button as dropdown/overlay clicked');
    }

    if (isCalloutVisible) {
      setClarityTag({ NewGlobalSearchShown: 'true' });
    }
    setCalloutVisible(!isCalloutVisible);
  }, [activity, isCalloutVisible]);

  useEffect(() => {
    if (hotKey === 'g+t' && count && $(`#${buttonId}`).is(':visible')) {
      toggleIsCalloutVisible();
    }
  // don't put toggleIsCalloutVisible into the dependency array, otherwise it will cause infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotKey, count, buttonId]);

  return (
    <Callout
      content={<StyledGlobalSearch
        isNewSearchBox="true"
        siteMap={siteMap}
        renderSearchResults={renderSearchResults}
        afterDismissSearchResult={toggleIsCalloutVisible}
        onCopilotClick={onCopilotClick}
        serverContext={serverContext}
      />}
      popoverId="global-search-callout"
      popoverClassName={classes.popover}
      placement="left"
      closeButton={false}
      show={isCalloutVisible}
      onClose={toggleIsCalloutVisible}
      focusLockContent
    >
      <SearchBar
        id={buttonId}
        onClick={toggleIsCalloutVisible}
        responsiveSize={responsiveSize}
        text={isCalloutVisible ? '' : i18n.getString(_TL_('Search'))}
        isActive={isCalloutVisible}
      />
    </Callout>
  );
}, ShortcutType.GlobalSearch)));

const toolbarElementCommonPropTypes = {
  classes: PropTypes.shape(mapObject(
    getToolbarElementsStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  styles: PropTypes.shape({
    left: PropTypes.number.isRequired,
  }).isRequired,
  responsiveSize: PropTypes.string.isRequired,
  helpProps: PropTypes.shape({
    showGetHelp: PropTypes.bool,
    showGetStarted: PropTypes.bool,
    showCommunity: PropTypes.bool,
    showLegacyChat: PropTypes.bool,
    customizedMenuItems: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      onClick: PropTypes.func,
      externalLink: PropTypes.string,
      children: PropTypes.arrayOf({}),
      showTag: PropTypes.string,
    })),
  }),
};

const toolbarElementDefaultProps = {
  styles: {
    left: 0,
  },
  helpProps: {
    showGetHelp: true,
    showGetStarted: true,
    showCommunity: true,
    customizedMenuItems: [],
  },
};

UnthemedImportBar.propTypes = defaults({}, toolbarElementCommonPropTypes);
UnthemedReportsBar.propTypes = defaults({}, toolbarElementCommonPropTypes);
UnthemedHelpBar.propTypes = defaults({}, toolbarElementCommonPropTypes);

UnthemedImportBar.defaultProps = defaults({}, toolbarElementDefaultProps);
UnthemedReportsBar.defaultProps = defaults({}, toolbarElementDefaultProps);
UnthemedHelpBar.defaultProps = defaults({}, toolbarElementDefaultProps);

const siteMapProcessors = [
  processor.processPermissions,
  processor.processAccountLinkPermission,
  processor.processPath,
];
const toolsSiteMapProcessors = [
  processor.processPermissions,
  processor.processAccountLinkPermission,
  processor.processPath,
  processor.processSharedLibrary,
];

export const ImportBar = withImportSiteMap(
  withDefaultStyles(UnthemedImportBar, getToolbarElementsStyles),
  siteMapProcessors
);

export const smartImportBar = withFlatSmartImportSiteMap(
  withDefaultStyles(UnthemedImportBar, getToolbarElementsStyles),
  siteMapProcessors
);

export const ReportsBar = withReportsSiteMap(
  withDefaultStyles(UnthemedReportsBar, getToolbarElementsStyles),
  siteMapProcessors
);

export const HelpBar = withDefaultStyles(UnthemedHelpBar, getToolbarElementsStyles);

export const SearchDropdownButton = withDefaultStyles(
  UnthemedSearchDropdownButton,
  getToolbarElementsStyles
);

const UnthemedToolsBar = getUnthemedToolsBar();

export const ToolsBar = withFlatToolsSiteMap(
  withDefaultStyles(UnthemedToolsBar, getToolbarElementsStyles),
  toolsSiteMapProcessors
);

export const getCustomizedToolsBar = ({
  TopMenuComponent = ReturnMenuItem,
  siteMapHoc = withFlatToolsSiteMap,
  siteMapCustomProcessors = toolsSiteMapProcessors,
  siteMapRootId = null,
} = {}) => {
  const UToolsbar = getUnthemedToolsBar(TopMenuComponent);
  return siteMapHoc(
    withDefaultStyles(UToolsbar, getToolbarElementsStyles),
    siteMapCustomProcessors, siteMapRootId
  );
};
